import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Users, Plus, Trash2, Pencil, Shield, Eye, Edit3 } from "lucide-react";
import PortalLayout from "@/components/portal/PortalLayout";
import { useToast } from "@/components/ui/use-toast";

interface TeamMember {
  email: string;
  role: "admin" | "editor" | "viewer";
  invited_at: string;
}

interface TeamData {
  team_name: string;
  members: TeamMember[];
}

const STORAGE_KEY = "intentdrive_team";

const DEFAULT_TEAM: TeamData = {
  team_name: "My Agency",
  members: [],
};

const ROLE_CONFIG = {
  admin: {
    label: "Admin",
    color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    icon: Shield,
    description: "Full access, can manage team",
  },
  editor: {
    label: "Editor",
    color: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    icon: Edit3,
    description: "Can create/edit routers and verticals",
  },
  viewer: {
    label: "Viewer",
    color: "bg-slate-500/20 text-slate-400 border-slate-500/30",
    icon: Eye,
    description: "Read-only access to analytics and sessions",
  },
} as const;

function loadTeam(): TeamData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { ...DEFAULT_TEAM };
}

function saveTeam(data: TeamData) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

const PortalTeam = () => {
  const { toast } = useToast();
  const [team, setTeam] = useState<TeamData>(loadTeam);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(team.team_name);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "editor" | "viewer">("viewer");

  useEffect(() => {
    saveTeam(team);
  }, [team]);

  const handleSaveName = () => {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    setTeam((prev) => ({ ...prev, team_name: trimmed }));
    setEditingName(false);
    toast({ title: "Team name updated" });
  };

  const handleInvite = () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast({ title: "Invalid email", variant: "destructive" });
      return;
    }
    if (team.members.some((m) => m.email === email)) {
      toast({ title: "Member already exists", variant: "destructive" });
      return;
    }
    setTeam((prev) => ({
      ...prev,
      members: [
        ...prev.members,
        { email, role: inviteRole, invited_at: new Date().toISOString() },
      ],
    }));
    setInviteEmail("");
    setInviteRole("viewer");
    setInviteOpen(false);
    toast({ title: "Member invited", description: email });
  };

  const handleRemove = (email: string) => {
    setTeam((prev) => ({
      ...prev,
      members: prev.members.filter((m) => m.email !== email),
    }));
    toast({ title: "Member removed" });
  };

  const handleRoleChange = (email: string, newRole: "admin" | "editor" | "viewer") => {
    setTeam((prev) => ({
      ...prev,
      members: prev.members.map((m) =>
        m.email === email ? { ...m, role: newRole } : m
      ),
    }));
    toast({ title: "Role updated" });
  };

  return (
    <PortalLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="h-6 w-6 text-emerald-400" />
            Team Management
          </h1>
          <p className="text-slate-400 mt-1">
            Manage your team members and their access levels.
          </p>
        </div>

        {/* Team Name */}
        <Card className="bg-slate-900 border-slate-800 p-6">
          <Label className="text-sm font-medium text-slate-300 mb-2 block">
            Team Name
          </Label>
          {editingName ? (
            <div className="flex items-center gap-3">
              <Input
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white max-w-sm"
                onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
                autoFocus
              />
              <Button
                size="sm"
                onClick={handleSaveName}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditingName(false);
                  setNameInput(team.team_name);
                }}
                className="text-slate-400 hover:text-white"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="text-lg font-semibold text-white">
                {team.team_name}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setNameInput(team.team_name);
                  setEditingName(true);
                }}
                className="text-slate-400 hover:text-emerald-400"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </Card>

        {/* Role Descriptions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(Object.keys(ROLE_CONFIG) as Array<keyof typeof ROLE_CONFIG>).map(
            (role) => {
              const cfg = ROLE_CONFIG[role];
              return (
                <Card
                  key={role}
                  className="bg-slate-900 border-slate-800 p-4 flex items-start gap-3"
                >
                  <cfg.icon className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white">{cfg.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {cfg.description}
                    </p>
                  </div>
                </Card>
              );
            }
          )}
        </div>

        {/* Members Table */}
        <Card className="bg-slate-900 border-slate-800">
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <h2 className="text-lg font-semibold text-white">
              Members ({team.members.length})
            </h2>
            <Button
              onClick={() => setInviteOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1" /> Invite Member
            </Button>
          </div>

          {team.members.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>No team members yet. Invite someone to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">Email</TableHead>
                  <TableHead className="text-slate-400">Role</TableHead>
                  <TableHead className="text-slate-400">Joined</TableHead>
                  <TableHead className="text-slate-400 text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {team.members.map((member) => (
                  <TableRow
                    key={member.email}
                    className="border-slate-800 hover:bg-slate-800/50"
                  >
                    <TableCell className="text-white font-medium">
                      {member.email}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={member.role}
                        onValueChange={(val) =>
                          handleRoleChange(
                            member.email,
                            val as "admin" | "editor" | "viewer"
                          )
                        }
                      >
                        <SelectTrigger className="w-32 bg-slate-800 border-slate-700 text-white h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="editor">Editor</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-slate-400">
                      {new Date(member.invited_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemove(member.email)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Invite Dialog */}
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogContent className="bg-slate-900 border-slate-800 text-white">
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-slate-300">Email Address</Label>
                <Input
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  onKeyDown={(e) => e.key === "Enter" && handleInvite()}
                />
              </div>
              <div>
                <Label className="text-slate-300">Role</Label>
                <Select
                  value={inviteRole}
                  onValueChange={(val) =>
                    setInviteRole(val as "admin" | "editor" | "viewer")
                  }
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {ROLE_CONFIG[inviteRole].description}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setInviteOpen(false)}
                className="text-slate-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleInvite}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Send Invite
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PortalLayout>
  );
};

export default PortalTeam;
